import { CustomWidgetCollection, Question, Serializer } from "survey-core";

// Create a custom question type
class DynamicTableQuestion extends Question {
  override getType() {
    return "dynamictable";
  }

  get dataSource() {
    return this.getPropertyValue("dataSource");
  }
  set dataSource(newValue) {
    this.setPropertyValue("dataSource", newValue);
  }

  get enableSearch() {
    return this.getPropertyValue("enableSearch");
  }
  set enableSearch(newValue) {
    this.setPropertyValue("enableSearch", newValue);
  }

  get multiSelect() {
    return this.getPropertyValue("multiSelect");
  }
  set multiSelect(newValue) {
    this.setPropertyValue("multiSelect", newValue);
  }
  
  get simpleMultiSelect() {
    return this.getPropertyValue("simpleMultiSelect");
  }
  set simpleMultiSelect(newValue) {
    this.setPropertyValue("simpleMultiSelect", newValue);
  }
  
  get selectionKeyField() {
    return this.getPropertyValue("selectionKeyField");
  }
  set selectionKeyField(newValue) {
    this.setPropertyValue("selectionKeyField", newValue);
  }
  
  get persistSelections() {
    return this.getPropertyValue("persistSelections");
  }
  set persistSelections(newValue) {
    this.setPropertyValue("persistSelections", newValue);
  }

  get pageSize() {
    return this.getPropertyValue("pageSize");
  }
  set pageSize(newValue) {
    this.setPropertyValue("pageSize", newValue);
  }

  get pageSizeOptions() {
    return this.getPropertyValue("pageSizeOptions");
  }
  set pageSizeOptions(newValue) {
    this.setPropertyValue("pageSizeOptions", newValue);
  }

  get defaultSortColumn() {
    return this.getPropertyValue("defaultSortColumn");
  }
  set defaultSortColumn(newValue) {
    this.setPropertyValue("defaultSortColumn", newValue);
  }

  get defaultSortDirection() {
    return this.getPropertyValue("defaultSortDirection");
  }
  set defaultSortDirection(newValue) {
    this.setPropertyValue("defaultSortDirection", newValue);
  }

  get striped() {
    return this.getPropertyValue("striped");
  }
  set striped(newValue) {
    this.setPropertyValue("striped", newValue);
  }

  get dense() {
    return this.getPropertyValue("dense");
  }
  set dense(newValue) {
    this.setPropertyValue("dense", newValue);
  }

  get rowHover() {
    return this.getPropertyValue("rowHover");
  }
  set rowHover(newValue) {
    this.setPropertyValue("rowHover", newValue);
  }

  get pageable() {
    return this.getPropertyValue("pageable");
  }
  set pageable(newValue) {
    this.setPropertyValue("pageable", newValue);
  }

  get filters() {
    return this.getPropertyValue("filters");
  }
  set filters(newValue) {
    this.setPropertyValue("filters", newValue);
  }

  get enablePrint() {
    return this.getPropertyValue("enablePrint");
  }
  set enablePrint(newValue) {
    this.setPropertyValue("enablePrint", newValue);
  }

  get focusSearchOnLoad() {
    return this.getPropertyValue("focusSearchOnLoad");
  }
  set focusSearchOnLoad(newValue) {
    this.setPropertyValue("focusSearchOnLoad", newValue);
  }
}

// Register the question type with properties
Serializer.addClass(
  "dynamictable",
  [
    {
      name: "dataSource",
      type: "string",
      default: "",
      category: "general",
      displayName: "Data Source Key"
    },
    {
      name: "enableSearch",
      type: "boolean",
      default: false,
      category: "general",
      displayName: "Enable Search Bar"
    },
    {
      name: "multiSelect",
      type: "boolean",
      default: false,
      category: "general",
      displayName: "Enable Row Selection"
    },
    {
      name: "simpleMultiSelect",
      type: "boolean",
      default: false,
      category: "general",
      displayName: "(old) Simple Multi-Select Mode"
    },
    {
      name: "selectionKeyField",
      type: "string",
      default: "",
      category: "general",
      displayName: "Selection Key Field"
    },
    {
      name: "persistSelections",
      type: "boolean",
      default: true,
      category: "general",
      displayName: "Remember Selections Across Pages"
    },
    {
      name: "pageSize",
      type: "number",
      default: 10,
      category: "general",
      displayName: "Initial Page Size"
    },
    {
      name: "pageSizeOptions",
      type: "string",
      default: "[5, 10, 25, 50, 100]",
      category: "general",
      displayName: "Page Size Options"
    },
    {
      name: "defaultSortColumn",
      type: "string",
      default: "",
      category: "general",
      displayName: "Default Sort Column"
    },
    {
      name: "defaultSortDirection",
      type: "string",
      default: "asc",
      choices: ["asc", "desc"],
      category: "general",
      displayName: "Default Sort Direction"
    },
    {
      name: "striped",
      type: "boolean",
      default: true,
      category: "appearance",
      displayName: "Alternating Row Colors"
    },
    {
      name: "dense",
      type: "boolean",
      default: false,
      category: "appearance",
      displayName: "Compact Row Height"
    },
    {
      name: "rowHover",
      type: "boolean",
      default: true,
      category: "appearance",
      displayName: "Highlight Rows on Hover"
    },
    {
      name: "pageable",
      type: "boolean",
      default: true,
      category: "general",
      displayName: "(old) Enable Pagination"
    },
    {
      name: "filters",
      type: "string",
      default: "[]",
      category: "general",
      displayName: "(old) Advanced Filters"
    },
    {
      name: "enablePrint",
      type: "boolean",
      default: false,
      category: "general",
      displayName: "Enable Print Button"
    },
    {
      name: "focusSearchOnLoad",
      type: "boolean",
      default: false,
      category: "general",
      displayName: "Focus Search on Load"
    }
  ],
  function() {
    return new DynamicTableQuestion("dynamictable");
  },
  "question"
);

// Define the custom widget - creates real DynamicTableComponent
export const dynamicTableQuestion = {
  name: "dynamictable",
  title: "Dynamic Table",
  iconName: "icon-table",
  category: "Custom Questions",
  widgetIsLoaded: () => true,
  isFit: (question: any) => question.getType() === "dynamictable",
  activatedByChanged: (activatedBy: string) => activatedBy === "property",
  isDefaultRender: false,
  htmlTemplate: "<div class='dynamic-table-container'></div>",
  
  // Create the actual DynamicTableComponent
  afterRender: async (question: any, element: any) => {
    if (!question || !element) return;

    
    // Get container
    const container = element.querySelector('.dynamic-table-container');
    
    if (!container) {
      console.error('[DynamicTable] Container not found');
      return;
    }

    try {
      // Get Angular services from window (set by dynamic-form component)
      const injector = (window as any).surveyInjector;
      const appRef = (window as any).surveyAppRef;
      const environmentInjector = (window as any).surveyEnvInjector;
      
      if (!injector || !appRef || !environmentInjector) {
        console.error('[DynamicTable] Angular services not available');
        return;
      }

      // Import and create the DynamicTableComponent
      const { DynamicTableComponent } = await import('../components/dynamic-table/dynamic-table.component');
      const { createComponent } = await import('@angular/core');
      
      
      // Create the component
      const componentRef = createComponent(DynamicTableComponent, {
        environmentInjector: environmentInjector,
        hostElement: container,
        elementInjector: injector
      });

      // --- Ensure SurveyJS context is detected ---
      try {
        container.setAttribute('surveyjs-component', 'true');
        container.surveyJSQuestionData = question;
      } catch (error) {
        console.error('[DynamicTable] Error setting SurveyJS context attributes:', error);
      }
      // --- End SurveyJS context fix ---

      const instance = componentRef.instance;
      
      if (!instance) {
        console.error('[DynamicTable] Component instance is undefined');
        return;
      }
      
      // Parse pageSizeOptions properly
      let parsedPageSizeOptions = '[5, 10, 25, 50, 100]'; // default
      if (question.pageSizeOptions) {
        try {
          if (typeof question.pageSizeOptions === 'string') {
            // It's already a JSON string, use it as-is
            parsedPageSizeOptions = question.pageSizeOptions;
          } else if (Array.isArray(question.pageSizeOptions)) {
            // It's already an array, stringify it
            parsedPageSizeOptions = JSON.stringify(question.pageSizeOptions);
          } else {
            // Try to parse it as JSON
            parsedPageSizeOptions = JSON.stringify(question.pageSizeOptions);
          }
        } catch (error) {
          console.error('[DynamicTable] Error parsing pageSizeOptions in SurveyJS widget:', error);
          parsedPageSizeOptions = '[5, 10, 25, 50, 100]';
        }
      }
      
      
      // Configure the component with question properties using the ComponentInputBase pattern
      const inputConfig: Record<string, { type: string; value: unknown }> = {
        dataSource: { type: 'text', value: question.dataSource || '' },
        enableSearch: { type: 'boolean', value: question.enableSearch === true },
        multiSelect: { type: 'boolean', value: question.multiSelect === true },
        simpleMultiSelect: { type: 'boolean', value: question.simpleMultiSelect === true },
        selectionKeyField: { type: 'text', value: question.selectionKeyField || '' },
        persistSelections: { type: 'boolean', value: question.persistSelections !== false },
        pageable: { type: 'boolean', value: true }, // Always enable pagination for SurveyJS questions
        pageSize: { type: 'number', value: question.pageSize || 10 },
        pageSizeOptions: { type: 'json', value: parsedPageSizeOptions },
        defaultSortColumn: { type: 'text', value: question.defaultSortColumn || '' },
        defaultSortDirection: { type: 'text', value: question.defaultSortDirection || 'asc' },
        striped: { type: 'boolean', value: question.striped !== false },
        dense: { type: 'boolean', value: question.dense === true },
        rowHover: { type: 'boolean', value: question.rowHover !== false },
        enablePrint: { type: 'boolean', value: question.enablePrint === true },
        focusSearchOnLoad: { type: 'boolean', value: question.focusSearchOnLoad === true }
      };
      const questionFilters = question.filters;
      const hasQuestionFilters =
        Array.isArray(questionFilters) && questionFilters.length > 0
        || (typeof questionFilters === 'string' && questionFilters.trim() !== '' && questionFilters.trim() !== '[]');
      if (hasQuestionFilters) {
        inputConfig['filters'] = { type: 'json', value: questionFilters };
      }
      
      
      // Set the dynamicInput property to configure the component
      
      if (!('dynamicInput' in instance)) {
        console.error('[DynamicTable] Instance does not have dynamicInput property');
        return;
      }
      
      instance.dynamicInput = inputConfig;
      

      // Set up selection change handlers
      instance.rowSelectionChange.subscribe((selectedRows: any[]) => {
        
        if (question.simpleMultiSelect && question.selectionKeyField) {
          const selectedValues = selectedRows
            .map((row: any) => row[question.selectionKeyField])
            .filter((val: any) => val !== undefined);
          question.value = selectedValues;
        } else {
          question.value = selectedRows;
        }
      });

      instance.simpleSelectionChange.subscribe((selectedValues: any[]) => {
        question.value = selectedValues;
      });

      // Attach to Angular and initialize
      appRef.attachView(componentRef.hostView);
      
      // Initialize the component
      if (typeof instance.ngOnInit === 'function') {
        instance.ngOnInit();
      }
      
      // Force the component to check for SurveyJS context after inputs are set
      setTimeout(() => {
        if (typeof instance.checkForSurveyJSContext === 'function') {
          instance.checkForSurveyJSContext();
        }
      }, 50);
      
      // Trigger change detection
      componentRef.changeDetectorRef.detectChanges();
      
      // Store component reference for cleanup
      question._dynamicTableComponent = componentRef;
      
      
    } catch (error) {
      console.error('[DynamicTable] Error creating component:', error);
      
      // Fallback: show error message
      container.innerHTML = `
        <div style="padding: 20px; border: 1px solid #ccc; background: #f9f9f9; text-align: center;">
          <h3>Error loading Dynamic Table</h3>
          <p>Question: ${question.name}</p>
          <p>Data Source: ${question.dataSource || 'None'}</p>
          <small>Check console for details</small>
        </div>
      `;
    }
  },
  
  // Cleanup
  destroy: (question: any) => {
    
    if (question._dynamicTableComponent) {
      try {
        question._dynamicTableComponent.destroy();
      } catch (error) {
        console.error('[DynamicTable] Error destroying component:', error);
      }
    }
  }
};

// Register the widget
CustomWidgetCollection.Instance.add(dynamicTableQuestion);

// Make it available globally for use in the form component
(window as any).dynamicTableQuestion = dynamicTableQuestion; 